import { t } from "@/i18n";
import { Keyboard, X } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

interface ShortcutEntry {
  keys: string[];
  description: string;
}

const shortcuts: ShortcutEntry[] = [
  { keys: ["/"], description: "Go to Search" },
  { keys: ["Ctrl", "K"], description: "Go to Search" },
  { keys: ["?"], description: "Open shortcut help" },
];

export function ShortcutHelpModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }

      if (e.key === "?" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }

      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-70 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label={t("shortcuts.title")}
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        onClick={() => setOpen(false)}
        aria-label="Close shortcut help"
      />
      <div
        className="relative bg-bg-primary rounded-xl shadow-xl border border-border max-w-md w-full mx-4 p-6"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 text-text-primary">
            <Keyboard size={22} />
            <h2 className="text-lg font-bold">{t("shortcuts.title")}</h2>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="text-text-muted hover:text-text-primary transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <ul className="space-y-3">
          {shortcuts.map((s, i) => (
            <li key={i} className="flex items-center justify-between">
              <span className="text-sm text-text-muted">{s.description}</span>
              <span className="flex items-center gap-1">
                {s.keys.map((k) => (
                  <kbd
                    key={k}
                    className="px-2 py-0.5 rounded bg-bg-surface border border-border text-xs font-mono text-text-primary"
                  >
                    {k}
                  </kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>

        <p className="text-xs text-text-muted mt-5 text-center">
          {t("shortcuts.hint")}
        </p>
      </div>
    </div>
  );
}
